import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../api';
import { BLOG_CATEGORIES } from '../constants/categories';
import { toSlug, buildSlug } from '../utils/slug';
import '../App.css';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr.split('T')[0];
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function isHtmlContent(text) {
  return /<[a-z][\s\S]*>/i.test(text || '');
}

function stripMarkdown(text) {
  return text
    .replace(/<!--page-break-->/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .trim();
}

function stripHtml(text) {
  const div = document.createElement('div');
  div.innerHTML = text
    .replace(/<[^>]*class="rte-page-break"[^>]*>.*?<\/div>/gi, ' ')
    .replace(/<img[^>]*>/gi, ' ');
  return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

function truncate(text, maxLength = 130) {
  if (!text) return '';
  const plain = isHtmlContent(text) ? stripHtml(text) : stripMarkdown(text);
  return plain.length > maxLength ? plain.substring(0, maxLength) + '...' : plain;
}

function readTime(text) {
  if (!text) return 1;
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

const CATEGORY_ICONS = {
  'Fashion and Beauty': '👗',
  'Electronics': '📱',
  'Mobile': '📲',
  'Home': '🏠',
  'Food and Health': '🥗',
  'Others': '🏷️',
  'All': '✨',
};

const CATEGORY_COLORS = {
  'Fashion and Beauty': '#a855f7',
  'Electronics': '#3b82f6',
  'Mobile': '#0ea5e9',
  'Home': '#f59e0b',
  'Food and Health': '#22c55e',
  'Others': '#6366f1',
  'All': '#6366f1',
};

const CATEGORY_TINTS = {
  'Fashion and Beauty': 'linear-gradient(to top, rgba(168,85,247,0.65) 0%, rgba(168,85,247,0.15) 55%, transparent 100%)',
  'Electronics': 'linear-gradient(to top, rgba(59,130,246,0.65) 0%, rgba(59,130,246,0.15) 55%, transparent 100%)',
  'Mobile': 'linear-gradient(to top, rgba(14,165,233,0.65) 0%, rgba(14,165,233,0.15) 55%, transparent 100%)',
  'Home': 'linear-gradient(to top, rgba(245,158,11,0.65) 0%, rgba(245,158,11,0.15) 55%, transparent 100%)',
  'Food and Health': 'linear-gradient(to top, rgba(34,197,94,0.65) 0%, rgba(34,197,94,0.15) 55%, transparent 100%)',
  'Others': 'linear-gradient(to top, rgba(99,102,241,0.65) 0%, rgba(99,102,241,0.15) 55%, transparent 100%)',
};

// Module-level cache so blogs survive remounts (back navigation from detail).
// Cleared on full page reload, which matches user expectation.
let blogsPageCache = null;

function BlogsPage() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState(() => blogsPageCache?.blogs ?? []);
  const [loading, setLoading] = useState(() => !blogsPageCache);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeCategory, setActiveCategory] = useState(() => blogsPageCache?.activeCategory ?? 'All');
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => blogsPageCache?.lastEvaluatedKey ?? null);
  const [hasMore, setHasMore] = useState(() => blogsPageCache?.hasMore ?? true);
  const sentinelRef = useRef(null);
  const categoryChangeAt = useRef(Date.now());
  const hadCacheOnMount = useRef(!!blogsPageCache);

  // Persist latest state to cache on unmount (so back-nav restores it)
  useEffect(() => {
    return () => {
      blogsPageCache = { blogs, activeCategory, lastEvaluatedKey, hasMore };
    };
  });

  useEffect(() => {
    // Skip the initial fetch if we restored from cache for this same category
    if (hadCacheOnMount.current) {
      hadCacheOnMount.current = false;
      return;
    }
    setBlogs([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
    categoryChangeAt.current = Date.now();
    fetchBlogs(activeCategory, null, true);
  }, [activeCategory]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() - categoryChangeAt.current < 800) return;
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchBlogs(activeCategory, lastEvaluatedKey);
        }
      },
      { threshold: 0, rootMargin: '600px 0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, loading, lastEvaluatedKey, activeCategory]);

  async function fetchBlogs(category, lek, replace = false) {
    replace ? setLoading(true) : setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: 5 });
      if (category !== 'All') params.set('category', category);
      if (lek) params.set('lastEvaluatedKey', JSON.stringify(lek));
      const response = await fetch(`${API_ENDPOINTS.BLOGS}?${params}`);
      if (response.ok) {
        const data = await response.json();
        const list = (data.blogs || data).map(b => ({ ...b, blogId: b.blogId || b.id || b._id }));
        setBlogs(prev => replace ? list : [...prev, ...list]);
        setLastEvaluatedKey(data.lastEvaluatedKey || null);
        setHasMore(!!data.lastEvaluatedKey);
      }
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }

  const categories = ['All', ...BLOG_CATEGORIES];

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <Helmet>
        <title>Shopping Blogs — Flipkart, Amazon, Myntra &amp; Ajio Buying Guides | OfferLelo</title>
        <meta name="description" content="Expert shopping blogs &amp; buying guides for Flipkart, Amazon, Myntra and Ajio offers in India. Read tips on Electronics, Fashion, Mobile &amp; Food deals — updated regularly at OfferLelo." />
        <meta name="keywords" content="flipkart buying guide, amazon shopping tips, myntra fashion blog, ajio offers blog, online shopping guide India, electronics buying guide, mobile buying tips" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="geo.region" content="IN" />
        <meta property="og:title" content="Shopping Blogs — Flipkart, Amazon, Myntra & Ajio Buying Guides | OfferLelo" />
        <meta property="og:description" content="Expert shopping blogs & buying guides for Flipkart, Amazon, Myntra and Ajio offers in India." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.offerlelo.com/blogs" />
        <meta property="og:image" content="https://www.offerlelo.com/logo192.png" />
        <meta property="og:site_name" content="OfferLelo" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Shopping Blogs — Flipkart, Amazon, Myntra & Ajio Buying Guides" />
        <meta name="twitter:description" content="Expert shopping blogs & buying guides for Flipkart, Amazon, Myntra and Ajio offers in India." />
        <meta name="twitter:image" content="https://www.offerlelo.com/logo192.png" />
        <link rel="canonical" href="https://www.offerlelo.com/blogs" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Blogs — Shopping Tips & Buying Guides | OfferLelo",
          "description": "Read expert blogs on shopping tips, buying guides, electronics, fashion, food & health and more at OfferLelo India.",
          "url": "https://www.offerlelo.com/blogs",
          "publisher": {
            "@type": "Organization",
            "name": "OfferLelo",
            "logo": { "@type": "ImageObject", "url": "https://www.offerlelo.com/logo192.png" }
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.offerlelo.com" },
              { "@type": "ListItem", "position": 2, "name": "Blogs", "item": "https://www.offerlelo.com/blogs" }
            ]
          }
        })}</script>
      </Helmet>
      {loading && <div className="top-loading-bar" />}
      {/* Header */}
      <header className="modern-header">
        <div className="header-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/logo.svg" alt="OfferLelo Logo" />
          <span className="header-logo-text">OfferLelo</span>
        </div>
        <div className="header-actions">
          <button className="admin-btn" onClick={() => navigate('/')}>← Home</button>
        </div>
      </header>

      {/* Hero Section */}
      <div className="blogs-hero">
        <div className="blogs-hero-inner">
          <div className="blogs-hero-badge">✍️ OfferLelo Blog</div>
          <h1 className="blogs-hero-title">
            Expert Tips, Guides &amp; Deals
          </h1>
          <p className="blogs-hero-subtitle">
            Money-saving guides, product comparisons, and shopping tips crafted by our experts — updated daily.
          </p>
          <div className="blogs-hero-stats">
            <div className="blogs-hero-stat">
              <span className="blogs-hero-stat-num">100+</span>
              <span className="blogs-hero-stat-label">Articles</span>
            </div>
            <div className="blogs-hero-stat-divider" />
            <div className="blogs-hero-stat">
              <span className="blogs-hero-stat-num">6</span>
              <span className="blogs-hero-stat-label">Categories</span>
            </div>
            <div className="blogs-hero-stat-divider" />
            <div className="blogs-hero-stat">
              <span className="blogs-hero-stat-num">Free</span>
              <span className="blogs-hero-stat-label">Always</span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 16px 48px' }}>

        {/* Category Filter */}
        <div className="blogs-category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`blogs-cat-btn${activeCategory === cat ? ' active' : ''}`}
              style={activeCategory === cat
                ? { background: `linear-gradient(135deg, ${CATEGORY_COLORS[cat]}, ${CATEGORY_COLORS[cat]}bb)`, borderColor: 'transparent' }
                : { borderColor: `${CATEGORY_COLORS[cat]}50`, color: CATEGORY_COLORS[cat] }
              }
            >
              <span>{CATEGORY_ICONS[cat] || '📌'}</span>
              {cat}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        {loading ? (
          <div className="blog-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="blog-card-skeleton">
                <div className="skeleton" style={{ height: 210, width: '100%', borderRadius: 0 }} />
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div className="skeleton" style={{ height: 13, width: '30%', borderRadius: 20 }} />
                  <div className="skeleton" style={{ height: 20, width: '90%' }} />
                  <div className="skeleton" style={{ height: 20, width: '65%' }} />
                  <div className="skeleton" style={{ height: 13, width: '100%' }} />
                  <div className="skeleton" style={{ height: 13, width: '80%' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                    <div className="skeleton" style={{ height: 12, width: '45%' }} />
                    <div className="skeleton" style={{ height: 36, width: '38%', borderRadius: 10 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : blogs.length === 0 ? (
          <div className="blogs-empty-state">
            <div className="blogs-empty-icon">📭</div>
            <h3 className="blogs-empty-title">No articles found</h3>
            <p className="blogs-empty-sub">
              {activeCategory !== 'All'
                ? `Nothing published in "${activeCategory}" yet.`
                : 'No blogs available right now — check back soon!'}
            </p>
            {activeCategory !== 'All' && (
              <button className="blogs-empty-reset" onClick={() => setActiveCategory('All')}>
                Browse all articles
              </button>
            )}
          </div>
        ) : (
          <div className="blog-grid">
            {blogs.map((blog, index) => {
              const mins = readTime(blog.content);
              const isFeatured = index === 0;
              const tint = CATEGORY_TINTS[blog.category] || 'linear-gradient(to top, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.1) 50%, transparent 100%)';
              const catColor = CATEGORY_COLORS[blog.category] || '#6366f1';
              // Real, crawlable URL for this article.
              const blogUrl = `/blog/${toSlug(blog.category)}/${buildSlug(blog.title, blog.blogId)}`;
              const openBlog = (e) => {
                if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) return;
                if (e) { e.preventDefault(); e.stopPropagation(); }
                navigate(blogUrl, { state: { blog } });
              };
              return (
                <article
                  key={blog.blogId || blog.id}
                  className={`blog-card${isFeatured ? ' blog-card-featured' : ''}`}
                  onClick={openBlog}
                  aria-label={`Read blog: ${blog.title}`}
                >
                  {/* Thumbnail */}
                  {blog.image ? (
                    <div className="blog-card-img-wrap">
                      <img src={blog.image} alt={blog.title} className="blog-card-img" loading="lazy" decoding="async" width="400" height="210" />
                      <div className="blog-card-img-overlay" style={{ background: tint }} />
                      <span className="blog-card-category-overlay" style={{ background: catColor, color: '#fff' }}>{blog.category}</span>
                      <span className="blog-card-readtime">{mins} min read</span>
                    </div>
                  ) : (
                    <div className="blog-card-no-img" style={{ background: `linear-gradient(135deg, ${catColor}cc, ${catColor}66)` }}>
                      <div className="blog-card-no-img-pattern" />
                      <span className="blog-card-no-img-icon">
                        {CATEGORY_ICONS[blog.category] || '📝'}
                      </span>
                      <span className="blog-card-category-overlay" style={{ background: catColor, color: '#fff' }}>{blog.category}</span>
                      <span className="blog-card-readtime">{mins} min read</span>
                    </div>
                  )}

                  {/* Content */}
                  <div className="blog-card-content">
                    {isFeatured && <span className="blog-card-featured-label">⭐ Featured</span>}
                    <h3 className="blog-card-title">
                      <a href={blogUrl} onClick={openBlog} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {blog.title}
                      </a>
                    </h3>
                    <p className="blog-card-excerpt">{truncate(blog.content, isFeatured ? 180 : 110)}</p>
                    <div className="blog-card-meta">
                      <span className="blog-card-avatar" style={{ background: `linear-gradient(135deg, ${catColor}, ${catColor}99)` }}>
                        {(blog.author || 'A')[0].toUpperCase()}
                      </span>
                      <span className="blog-card-author-name">{blog.author}</span>
                      <span className="blog-card-dot">·</span>
                      <span className="blog-card-date">{formatDate(blog.createdOn)}</span>
                    </div>
                    <a
                      className="blog-card-btn"
                      href={blogUrl}
                      style={{ background: `linear-gradient(135deg, ${catColor}, ${catColor}cc)`, textDecoration: 'none' }}
                      onClick={openBlog}
                    >
                      Read Article <span className="blog-card-btn-arrow">→</span>
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div ref={sentinelRef} style={{ height: 32 }} />
        {loadingMore && (
          <div style={{ textAlign: 'center', padding: '16px 0', color: '#6366f1', fontWeight: 600, fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <span style={{ display: 'inline-block', width: 18, height: 18, border: '3px solid #e0e7ff', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            Loading more articles...
          </div>
        )}
      </div>

      {/* Footer mini */}
      <div style={{ background: '#1e1b4b', color: '#6366f1', textAlign: 'center', padding: '16px', fontSize: '0.82rem' }}>
        © {new Date().getFullYear()} OfferLelo · <a href="mailto:offerleleo365@gmail.com" style={{ color: '#818cf8', textDecoration: 'none' }}>offerleleo365@gmail.com</a>
      </div>

      {blogs.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": "OfferLelo Blog Articles",
            "numberOfItems": blogs.length,
            "itemListElement": blogs.map((b, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "url": `https://www.offerlelo.com/blog/${toSlug(b.category)}/${buildSlug(b.title, b.blogId)}`,
              "name": b.title
            }))
          })}</script>
        </Helmet>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default BlogsPage;
